import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useCalls } from "@/lib/calls";
import { useSession } from "@/lib/session";

const PAGE_TITLES: Array<{ href: string; label: string }> = [
  { href: "/app/softphone", label: "Softphone" },
  { href: "/app/live", label: "Live calls" },
  { href: "/app/users", label: "Users" },
  { href: "/app/extensions", label: "Extensions" },
  { href: "/app/inbound", label: "Inbound call management" },
  { href: "/app/cdr", label: "CDR report" },
  { href: "/app/bulk", label: "Bulk calling" },
  { href: "/app/contacts", label: "Contacts" },
  { href: "/app/apps", label: "MQTT / PBX" },
  { href: "/app/developers", label: "Developers" },
  { href: "/app/profile", label: "Profile" },
  { href: "/app", label: "Dashboard" },
];

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

function pageTitle(path: string) {
  const hit = PAGE_TITLES.find((p) =>
    p.href === "/app" ? path === "/app" : path === p.href || path.startsWith(`${p.href}/`),
  );
  return hit?.label || "Console";
}

export function TopBar() {
  const { pathname: path } = useLocation();
  const { user, orgName } = useSession();
  const calls = useCalls();
  const [clock, setClock] = useState("");
  const [mqttOk, setMqttOk] = useState<boolean | null>(null);

  useEffect(() => {
    const tick = () =>
      setClock(
        new Date().toLocaleString(undefined, {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let stop = false;
    async function load() {
      try {
        const row = await api<{ connected?: boolean }>("/pbx/mqtt/health");
        if (!stop) setMqttOk(Boolean(row?.connected));
      } catch {
        if (!stop) setMqttOk(false);
      }
    }
    void load();
    const id = setInterval(() => void load(), 20000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, []);

  const liveCount = calls.live.length;
  const notice = (() => {
    if (calls.myCall) {
      const who =
        calls.popup?.callerName ||
        calls.popup?.callerPhone ||
        calls.myCall.to ||
        calls.myCall.from;
      const dir = String(calls.myCall.direction || "").toLowerCase() === "inbound" ? "Incoming" : "Outgoing";
      return {
        tone: "call",
        text: `${dir} · ${who}`,
      };
    }
    if (mqttOk === false) return { tone: "warn", text: "MQTT broker offline — check server connection." };
    return { tone: "ok", text: `Line ${calls.lineStatus || "idle"} · ready for click-to-call.` };
  })();

  if (!user) return null;

  return (
    <header className="topbar">
      <div className="topbar-brand">
        <p className="topbar-kicker">{orgName || "iPBX"}</p>
        <h1 className="topbar-title">{pageTitle(path)}</h1>
      </div>
      <div className={`topbar-notice ${notice.tone}`} role="status">
        <i />
        <span>{notice.text}</span>
      </div>
      <div className="topbar-meta">
        <span className={`topbar-chip ${liveCount ? "live" : ""}`}>
          <b>{liveCount}</b> live
        </span>
        <span className={`topbar-chip ${mqttOk ? "on" : mqttOk === false ? "off" : ""}`}>
          PBX {mqttOk ? "online" : mqttOk === false ? "offline" : "…"}
        </span>
        <span className={`topbar-chip ${user.extension ? "on" : "off"}`}>
          Ext {user.extension || "—"}
        </span>
        <span className="topbar-chip">Hard phone</span>
        <span className="topbar-clock">{clock}</span>
        <Link to="/app/profile" className="topbar-user" title="Profile">
          <span className="topbar-avatar">{initials(user.name)}</span>
          <span className="topbar-user-name">{user.name.split(" ")[0]}</span>
        </Link>
      </div>
    </header>
  );
}
